import * as React from "react";
import { useState, useCallback, useEffect } from "react";
import axios from "axios";

import Prompt from "./prompt.component";
import { FileError, FileRejection, useDropzone } from "react-dropzone";

const API_HOST = process.env.REACT_APP_API_URL || "localhost:3030/api/";
const DESKTOP_IMAGE_SIZE = "890x500";
const MOBILE_IMAGE_SIZE = "400x230";
const IMAGE_HOST =
  process.env.REACT_APP_MEDIA_URL || "http://beta-assets.qesheq.com";
interface PropsInterface {
  isOpen: boolean;
  toggleOpen: (value: boolean) => void;
  onUpload: (urls: ImageSizeUrl) => void;
  imageRole: string;
  uploadUrl?: string;
  accessToken?: string | null;
}
export interface ImageSizeUrl {
  desktop: string;
  mobile: string;
  raw: string;
}
const formattedImageUrl = ({
  rawUrl,
  directory,
}: {
  rawUrl?: string;
  directory: string;
}): ImageSizeUrl | null => {
  if (!rawUrl) return null;
  const imagePathChunks = rawUrl.split("/");
  const imageName = imagePathChunks[imagePathChunks.length - 1];
  return {
    desktop: `${IMAGE_HOST}resized/${DESKTOP_IMAGE_SIZE}/${imageName}`,
    mobile: `${IMAGE_HOST}resized/${MOBILE_IMAGE_SIZE}/${imageName}`,
    raw: `${IMAGE_HOST}raw/${directory}/${imageName}`,
  };
};

const ImagePrompt = ({
  isOpen,
  toggleOpen,
  onUpload,
  imageRole,
  uploadUrl = `${API_HOST}images`,
  accessToken,
}: PropsInterface): JSX.Element => {
  const [image, setImage] = useState<File | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [dropzoneText, setDropzoneText] = useState(
    "Drag and drop your image here, or click to browse your disk.."
  );
  const [errors, setErrors] = useState<Array<string>>([]);

  const upload = useCallback(
    async (image: File) => {
      setIsLoading(true);

      try {
        const formData = new FormData();
        formData.append("image", image);

        const uploadedImage = await axios.post(
          `${uploadUrl}?role=${imageRole}`,
          formData,
          {
            headers: {
              accept: "application/json",
              authorization: accessToken ? `Bearer ${accessToken}` : undefined,
            },
          }
        );
        const imageUrls = formattedImageUrl({
          rawUrl: uploadedImage?.data?.fileName,
          directory: imageRole.split(":")[0],
        });
        if (imageUrls) {
          // TODO: find a better way to wait for the image to be resized
          onUpload(imageUrls);
          setIsLoading(false);
        }
      } catch (error) {
        console.log("Error uploading description image", error);
        setIsLoading(false);
        throw error;
      }
    },
    [imageRole, onUpload]
  );

  const handleOnDrop = useCallback((images: File[]) => {
    if (images.length > 1) {
      alert("You can only drop one image");
    } else {
      upload(images[0]);
    }
  }, []);

  const handleOnDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      setErrors([]);
      const distinctErrors: string[] = [];
      fileRejections.forEach((fileRejection: FileRejection) => {
        fileRejection.errors.forEach((error: FileError) => {
          if (!distinctErrors.includes(error.message)) {
            distinctErrors.push(error.message);
          }
        });
      });
      setErrors(distinctErrors);
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleOnDrop,
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    maxFiles: 1,
    multiple: false,
    maxSize: 5242880,
    onDropRejected: handleOnDropRejected,
  });

  useEffect(() => {
    if (isDragActive) {
      setDropzoneText("Drop it here!");
    } else {
      setDropzoneText("Drop image, or click to select");
    }
  }, [isDragActive]);

  useEffect(() => {
    if (image) {
      upload(image);
      setImage(null);
    }
  }, [image, upload]);

  return (
    <Prompt isOpen={isOpen} isLoading={isLoading} toggleOpen={toggleOpen}>
      <div
        className="file-dropzone flex-container align-items-center space-around"
        {...getRootProps()}
      >
        <div className="dropzone-content flex-container space-around">
          <i className="dropzone-icon " aria-hidden="true" />
          <span className="dropzone-text">{dropzoneText}</span>
          {errors.length ? (
            <div className="dropzone-errors">
              {errors.map((error: string) => (
                <span className="dropzone-error-message">{error}</span>
              ))}
            </div>
          ) : null}
        </div>
        <input {...getInputProps()} />
      </div>
    </Prompt>
  );
};

export default ImagePrompt;
